'use client';

import Link from 'next/link';
import { usePathname, useRouter } from 'next/navigation';
import { useCallback, useEffect, useState } from 'react';
import { Toast } from '@/ui/common/Toast/Toast';
import { IconButton } from '@/ui/common/Buttons/IconButton';
import { LinkIcon } from '@/ui/common/Links/LinkIcon';
import { Drawer } from '@/ui/common/Drawer/Drawer';
import { CountdownTimerPopup } from '@/ui/common/Popups/CountdownTimerPopup';
import { ArrowBack } from '@/ui/common/Icons/ArrowBack';
import { MenuButton } from '@/ui/common/Buttons/MenuButton';
import { WaverezLogo } from '@/ui/common/Icons/WaverezLogo';
import { useCartStore } from '@/service/state/Cart/useCartStore';
import { useAttractionsStore } from '@/service/state/Attractions/useAttractionsStore';
import { POPUP_OPENING_TIME_MINUTES } from '@/service/constants/variables';
import { ECartActionType } from '@/service/state/Cart/domain';
import { cn } from '@/service/utils/cn';
import { ETripView } from '@/domain/Activities.domain';
import { ToggleTheme } from '../../Layouts/core';
import { ToastScreen } from '../../CartContent/core/ToastScreen';
import { CloseIcon } from '@/ui/common/Icons/CloseIcon';
import { getCommonPhoneFormatURI } from '@/service/utils/getCommonPhoneFormat';
import { usePartnerStore } from '@/service/state/Partner/usePartnerStore';
import { SearchBox } from '../../SearchBox/SearchBox';

type TLightboxHeaderProps = {
    partnerId: string;
    partnerSlug: string;
    partnerWebsite: string;
    partnerLogoSrc: string;
    phoneNumber: string;
};

export const LightboxHeader = ({
    partnerId,
    partnerSlug,
    partnerLogoSrc,
    phoneNumber,
}: TLightboxHeaderProps) => {
    const router = useRouter();
    const pathname = usePathname();

    const [drawerIsOpen, setDrawerIsOpen] = useState(false);
    const [extended, setExtended] = useState(false);

    const { cart, getCart, extCart, success, cartToastIsShown, showThenHideCartToast } =
        useCartStore((state) => state);
    const { handleTripViewChange } = useAttractionsStore();
    const callToBookText = usePartnerStore((state) => state.texts?.['Call to Book']);

    useEffect(() => {
        setExtended(
            success.actionType === ECartActionType.EXTEND_TIME_FOR_CART && !!success.message
        );
    }, [success.actionType, success.message]);

    const fetchCart = useCallback(
        () => getCart({ partnerId }, ECartActionType.GET_CART),
        [getCart, partnerId]
    );
    const extendTimeByFiveMinutes = useCallback(() => extCart({ partnerId }), [extCart, partnerId]);
    const goToCheckoutPage = useCallback(() => {
        extendTimeByFiveMinutes();
        router.push(`/lightframe${partnerId}/checkout`);
    }, [partnerId, router, extendTimeByFiveMinutes]);
    const handleCalendarLink = useCallback(
        (view: ETripView) => {
            handleTripViewChange(view);
            setDrawerIsOpen(false);
        },
        [handleTripViewChange]
    );

    useEffect(() => {
        fetchCart();
    }, [fetchCart]);

    /* const { isSticky } = useStickyElementById('sticky-buttons', {
        offset: 20,
        offsetDesktop: 72,
        parentId: 'lightframe-container',
    });
    const isMainPage = pathname.split('/').length <= 3; */
    const isBillPage = pathname.includes('/bill/');
    /* const hideLogo = isSticky && isMainPage; */

    return (
        <>
            <Toast
                show={cartToastIsShown}
                screen={
                    <ToastScreen
                        title={'Your cart is empty!'}
                        description={
                            'Lets find some wonderful activities to do. Get ready to be inspired...'
                        }
                    />
                }
            />
            <div
                className={cn(
                    'sticky top-0 z-40 flex h-14 w-full flex-shrink-0 items-center justify-between bg-blue-main pl-4 pr-1 dark:bg-dark-3 md:h-16 md:px-8',
                    /* hideLogo ? 'z-40' :  */ 'z-50'
                )}
            >
                <div className="flex w-full justify-between md:mx-auto md:max-w-[1060px]">
                    <div className="relative flex shrink-0 items-center">
                        <IconButton
                            className="mr-0 cursor-pointer rounded-lg px-[4px] py-[10px] hover:bg-ash-hover md:mr-3"
                            onClick={() => setDrawerIsOpen(true)}
                            icon={
                                <img
                                    src="/icon/Menu.svg"
                                    className=" h-[16px] w-[24px] fill-blue-dark stroke-blue-dark"
                                    alt="Back"
                                />
                            }
                            data-test-id="menu-btn"
                        />
                        <LinkIcon
                            href={`/lightframe${partnerId}/`}
                            iconSrc={partnerLogoSrc}
                            className={cn(
                                'relative left-0 top-[-2px] mt-1 h-10 w-fit max-w-[148px] overflow-hidden rounded shadow-dark transition-transform md:top-[-4px] md:mt-2 md:h-12 md:max-w-[168px] [&>*:nth-child(1)]:w-auto'
                                /* hideLogo ? 'translate-y-[-150%] lg:translate-y-0' : '' */
                            )}
                            alt={partnerSlug}
                            data-test-id="partner-logo"
                            onClick={() => handleCalendarLink(ETripView.BROWSE_BY_ACTIVITY)}
                        />
                    </div>
                    <div className="flex w-full items-center md:w-fit">
                        <SearchBox isLightframe />
                        <LinkIcon
                            href={getCommonPhoneFormatURI(phoneNumber)}
                            target="_blank"
                            iconSrc="/icon/Phone.svg"
                            className={cn(
                                'relative ml-2 mr-2 h-[26px] w-[26px] shrink-0 py-1 transition-transform hover:py-0 md:h-8 md:w-8'
                                /* hideLogo ? 'translate-y-[-150%] sm:translate-y-0' : '' */
                            )}
                            alt="Phone"
                            data-test-id="phone-btn"
                        />
                        {!isBillPage && (
                            <LinkIcon
                                href={`/lightframe${partnerId}/cart/`}
                                onClick={(e) => {
                                    if (!cart?.cart_items.length) {
                                        e.preventDefault();
                                        showThenHideCartToast();
                                    }
                                }}
                                iconSrc="/icon/Cart.svg"
                                className="relative mr-2 h-[26px] w-[26px] shrink-0 py-1 hover:py-0 md:mr-8 md:h-8 md:w-8 xl:mr-0"
                                alt="Cart"
                                child={
                                    !!cart?.cart_items.length && (
                                        <span className="icon-status-badge">
                                            {cart.cart_items.length}
                                        </span>
                                    )
                                }
                                data-test-id="cart-btn"
                            />
                        )}
                        <IconButton
                            icon={
                                <CloseIcon className="darken bg-dark-2 hover:bg-dark-3 hover:shadow-dark" />
                            }
                            className={cn('rounded-full md:hidden')}
                            onClick={() => {
                                window.parent.postMessage(
                                    {
                                        type: 'wr_close_sign',
                                        items: cart?.cart_items?.length || 0,
                                        partnerId,
                                    },
                                    '*'
                                );
                            }}
                        />
                    </div>
                </div>
            </div>
            <Drawer
                customIdSuffix="sideMenuDrawer"
                open={drawerIsOpen}
                onClose={() => setDrawerIsOpen(false)}
                direction="left"
                className="overflow-hidden rounded-r-2xl"
                wrapperClassName="max-h-full !h-max"
            >
                <div
                    data-test-id="side-menu"
                    className={`relative h-screen w-[95vw] max-w-[344px] bg-white p-[14px_40px_78px] shadow-lightPopup transition-all duration-300 ease-out dark:bg-blue-main dark:shadow-darkCard md:h-[90vh]`}
                >
                    <div className="flex items-center justify-between border-b border-gray-4 pb-3 dark:border-blue-1">
                        <img src={partnerLogoSrc} className="h-10" alt="brand-logo" />
                        <IconButton
                            icon={
                                <ArrowBack className="cursor-pointer [&>*]:fill-dark-4 [&>*]:hover:fill-brand [&>*]:dark:fill-white" />
                            }
                            onClick={() => setDrawerIsOpen(false)}
                            data-test-id="back-btn"
                        />
                    </div>
                    <div className="pb-6 pt-7">
                        {!isBillPage && (
                            <Link
                                href={`/lightframe${partnerId}/cart`}
                                prefetch={false}
                                onClick={(e) => {
                                    if (!cart?.cart_items.length) {
                                        e.preventDefault();
                                        showThenHideCartToast();
                                    }
                                }}
                            >
                                <div className="relative">
                                    <MenuButton
                                        icon="Cart"
                                        label="Cart"
                                        onClick={() => setDrawerIsOpen(false)}
                                        data-test-id="menu-btn"
                                    />
                                    {!!cart?.cart_items.length && (
                                        <span className="icon-status-badge left-6 right-auto">
                                            {cart.cart_items.length}
                                        </span>
                                    )}
                                </div>
                            </Link>
                        )}
                        <Link href={`/lightframe${partnerId}/`} prefetch={false}>
                            <MenuButton
                                icon="Browse"
                                label="Browse by activity"
                                onClick={() => handleCalendarLink(ETripView.BROWSE_BY_ACTIVITY)}
                                data-test-id="browse-by-activity-btn"
                            />
                        </Link>
                        <Link href={`/lightframe${partnerId}/`} prefetch={false}>
                            <MenuButton
                                icon="Calendar"
                                label="Calendar view"
                                onClick={() => handleCalendarLink(ETripView.CALENDAR)}
                                data-test-id="calendar-view-btn"
                            />
                        </Link>
                    </div>
                    {!pathname.includes('/checkout') && !isBillPage && (
                        <div className="border-y border-gray-4 dark:border-blue-1">
                            <ToggleTheme />
                        </div>
                    )}
                    <Link
                        href={getCommonPhoneFormatURI(phoneNumber)}
                        target="_blank"
                        prefetch={false}
                        className="mt-7"
                    >
                        <MenuButton
                            icon="Phone"
                            label={callToBookText || 'Call to book'}
                            data-test-id="menu-phone-btn"
                        />
                    </Link>
                    <div
                        data-test-id="waverez-link"
                        className="tx-large-medium absolute bottom-9 left-0 flex w-full flex-col items-center text-gray-3"
                    >
                        <Link
                            href={`/lightframe${partnerId}/terms/`}
                            className="tx-large-medium mb-4 text-brand underline"
                            onClick={() => setDrawerIsOpen(false)}
                            prefetch={false}
                        >
                            Policies & Terms
                        </Link>
                        Powered By
                        <Link href="https://www.waverez.com" prefetch={false}>
                            <WaverezLogo className="h-6 [&>*:nth-child(1)]:fill-gray-3 [&>*:nth-child(1)]:dark:fill-ash [&>*:nth-child(10)]:fill-gray-3 [&>*:nth-child(10)]:dark:fill-ash [&>*:nth-child(2)]:fill-gray-3 [&>*:nth-child(2)]:dark:fill-ash [&>*:nth-child(7)]:fill-gray-3 [&>*:nth-child(7)]:dark:fill-ash [&>*:nth-child(8)]:fill-gray-3 [&>*:nth-child(8)]:dark:fill-ash [&>*:nth-child(9)]:fill-gray-3 [&>*:nth-child(9)]:dark:fill-ash" />
                        </Link>
                    </div>
                    <div className="absolute right-3 top-[40vh] h-[20vh] w-[6px] rounded-lg bg-gray-4 dark:bg-blue-1" />
                </div>
            </Drawer>
            {!isBillPage && (
                <CountdownTimerPopup
                    timeLeft={cart?.time_left && cart.time_left > 0 ? cart.time_left : 0}
                    onCountdownEnd={fetchCart}
                    popupOpeningTimeMinutes={POPUP_OPENING_TIME_MINUTES}
                    withActionButtons
                    closePopupAfterAction
                    countdownIsExtended={extended}
                    primaryButtonText="Extend time by 2 minutes"
                    secondaryButtonText="Checkout now"
                    onClickPrimaryButton={extendTimeByFiveMinutes}
                    onClickSecondaryButton={goToCheckoutPage}
                />
            )}
        </>
    );
};
